import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { style: {"display":"flex","flex-direction":"row"} }
const _hoisted_2 = { key: 1 }

import ListFilter from '@/components/common/ListFilter.vue';
import TranslatedState from '@/components/common/TranslatedState.vue';
import ContentItemForm from '@/components/content/ContentItemForm.vue';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import { Action, ActionType } from '@/models/action.model';
import { Hero } from '@/models/hero.model';
import { InfoGraphic } from '@/models/info-graphic.model';
import { getShortStatus, Initiative } from '@/models/initiative.model';
import { QuizQuestion } from '@/models/quiz-question.model';
import { TranslationState } from '@/models/translation.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { ContentStoreActions, ContentStoreState } from '@/stores/content.store';
import {
  TYPE_ACTION,
  TYPE_INFOGRAPHIC,
  NEW_ID,
  TYPE_INITIATIVE,
  TYPE_INITIATIVE_BY_CONTENT_EDITOR,
  TYPE_QUESTION,
} from '@/util/constants';
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

export interface ContentItem {
  id: string;
  itemId: number;
  type: string;
  typeName: string;
  published: boolean;
  title: string;
  contentItem: Action | QuizQuestion | Hero | InfoGraphic;
  translated?: TranslationState;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ContentList',
  setup(__props) {

const store = useStore<ShiftCMSState & { cns: ContentStoreState }>();
const events = useEvents();
const router = useRouter();

const infoGraphics = computed<InfoGraphic[]>(
  () => store.state.cns.infoGraphics,
);
const actions = computed<Action[]>(() => store.state.cns.actions);
const initiatives = computed<Initiative[]>(() => store.state.cns.initiatives);
const questions = computed<QuizQuestion[]>(() => store.state.cns.questions);

const content = computed<ContentItem[]>(() => {
  return [
    ...infoGraphics.value.map((i) => {
      return {
        id: `${i.id}_infographic`,
        itemId: i.id,
        type: TYPE_INFOGRAPHIC,
        typeName: 'InfoGraphic',
        published: true,
        title: i.title,
        contentItem: i,
        status: '-',
        translated: i.translated,
      };
    }),
    ...initiatives.value.map((i) => {
      return {
        id: `${i.id}_initaitive`,
        itemId: i.id,
        type: TYPE_INITIATIVE,
        typeName: 'Initiative',
        title: i.title,
        published: i.published,
        contentItem: i,
        status: i.status,
        translated: i.translated,
      };
    }),
    ...actions.value.map((i) => {
      return {
        id: `${i.id}_action`,
        itemId: i.id,
        type: TYPE_ACTION,
        typeName: 'Action',
        title: i.title,
        published: i.published,
        contentItem: i,
        status: '-',
        translated: i.translated,
      };
    }),
    ...questions.value.map((i) => {
      return {
        id: `${i.id}_question`,
        itemId: i.id,
        type: TYPE_QUESTION,
        typeName: 'Question',
        title: i.title,
        published: true,
        contentItem: i,
        status: '-',
        translated: i.translated,
      };
    }),
  ].sort((a, b) => (a.title > b.title ? -1 : 1));
});

const update = () => {
  store.dispatch(ContentStoreActions.GetActions);
  store.dispatch(ContentStoreActions.GetInfoGraphics);
  store.dispatch(ContentStoreActions.GetInitiatives);
  store.dispatch(ContentStoreActions.GetQuizQuestions);
};

onMounted(() => {
  events.on(Event.UpdateContentItem, update);
  events.on(Event.LanguageChanged, update);
  update();
});

onBeforeUnmount(() => {
  events.off(Event.UpdateContentItem, update);
  events.off(Event.LanguageChanged, update);
});

const headers = [
  { title: 'ID', key: 'itemId', width: '5%' },
  { title: 'Type', key: 'typeName', width: '10%' },
  { title: 'Title', key: 'title', width: '40%' },
  { title: 'Category', key: 'categories', width: '25%' },
  { title: 'Status', key: 'status', width: '10%' },
  { title: 'State', key: 'translated', sortable: false },
  { title: 'Story', key: 'story', sortable: false, width: '5%' },
];

const editItem = (
  _: MouseEvent,
  rowData: { item: { type: string; contentItem: ContentItem } },
) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(ContentItemForm),
    data: {
      type: rowData.item.type,
      contentItem: rowData.item.contentItem,
    },
  });
};

const goToStory = async (event: MouseEvent, type: string, id: number) => {
  event.stopPropagation();
  event.preventDefault();
  const story = await store.dispatch(ContentStoreActions.GetStory, {
    type,
    id,
  });

  if (story.id) {
    router.push(`/stories/${story.id}`);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: `This ${type} is not connected to any story`,
      type: 'info',
    });
  }
};

const addItem = (type: string) => {
  const contentItem = {};
  switch (type) {
    case TYPE_INFOGRAPHIC:
      Object.assign(contentItem, {
        id: NEW_ID,
        title: '',
        description: '',
        media: [],
      });
      break;
    case TYPE_ACTION:
      Object.assign(contentItem, {
        id: NEW_ID,
        title: '',
        categories: [],
        steps: [],
        media: [],
        notRelevantForlabels: [],
        type: ActionType.Action,
        difficulty: 0,
        impact: 0,
      });
      break;
    case TYPE_INITIATIVE:
      Object.assign(contentItem, {
        id: NEW_ID,
        title: '',
        description: '',
        published: false,
        mainCategory: {},
        categories: [],
        contacts: [],
        website: '',
        address: {
          street: '',
          house_number: '',
          postal_code: '',
          city: '',
          country: 'NL',
          isSpecific: true,
          alternative: [],
        },
      });
      break;
  }

  store.commit(Mutations.ShowDrawer, {
    component: markRaw(ContentItemForm),
    data: {
      type: type,
      contentItem,
    },
  });
};

const filter = ref<string>('');
const filterTitle = ref<string>('');
const filteredContent = computed(() =>
  content.value
    .filter((v) =>
      filter.value
        ? filter.value === TYPE_INITIATIVE_BY_CONTENT_EDITOR
          ? (v.contentItem as Initiative).createdByUser
          : v.type === filter.value
        : true,
    )
    .filter((v) =>
      filterTitle.value
        ? `${v.title || ''}`
            .toLowerCase()
            .indexOf(filterTitle.value.toLowerCase()) !== -1
        : true,
    ),
);

function updateFilter(filters: { filter: string; filterTitle: string }) {
  filter.value = filters.filter;
  filterTitle.value = filters.filterTitle;
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, null, {
    default: _withCtx(() => [
      _createElementVNode("h2", null, [
        _cache[3] || (_cache[3] = _createTextVNode("Content ")),
        _createVNode(CurrentLanguage)
      ]),
      _createVNode(ListFilter, {
        options: [
        { label: 'Actions', value: _unref(TYPE_ACTION) },
        { label: 'Infographics', value: _unref(TYPE_INFOGRAPHIC) },
        { label: 'Initiatives', value: _unref(TYPE_INITIATIVE) },
        { label: 'Content Editor', value: _unref(TYPE_INITIATIVE_BY_CONTENT_EDITOR) },
        { label: 'Questions', value: _unref(TYPE_QUESTION) },
      ],
        onFilter: updateFilter
      }, null, 8, ["options"]),
      _createVNode(_component_v_data_table, {
        density: "compact",
        headers: headers,
        items: filteredContent.value,
        "onClick:row": _withModifiers(editItem, ["prevent"]),
        itemsPerPage: "25",
        hover: ""
      }, {
        "item.translated": _withCtx(({ item }) => [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(TranslatedState, { item: item }, null, 8, ["item"]),
            _createVNode(_component_v_tooltip, { text: "This item is recommended" }, {
              activator: _withCtx(({ props }) => [
                (!!(item.contentItem as Initiative).recommended)
                  ? (_openBlock(), _createBlock(_component_v_icon, _mergeProps({ key: 0 }, props, {
                      icon: "mdi-thumb-up-outline",
                      color: "primary",
                      class: "ml-2"
                    }), null, 16))
                  : _createCommentVNode("", true)
              ]),
              _: 2
            }, 1024),
            _cache[4] || (_cache[4] = _createTextVNode("   ")),
            _createVNode(_component_v_tooltip, {
              text: 
              item.published
                ? 'This item is published'
                : 'This item is not published'
            
            }, {
              activator: _withCtx(({ props }) => [
                _createVNode(_component_v_icon, _mergeProps(props, {
                  icon: item.published ? 'mdi-eye-check' : 'mdi-eye-outline',
                  color: item.published ? 'green' : 'red'
                }), null, 16, ["icon", "color"])
              ]),
              _: 2
            }, 1032, ["text"])
          ])
        ]),
        "item.categories": _withCtx(({ item }) => [
          _createTextVNode(_toDisplayString((item.contentItem as any).mainCategory
            ? (item.contentItem as any).mainCategory.title
            : (item.contentItem as any).categories &&
              (item.contentItem as any).categories[0]
            ? (item.contentItem as any).categories[0].title
            : ''), 1)
        ]),
        "item.story": _withCtx(({ item }) => [
          _createVNode(_component_v_btn, {
            size: "x-small",
            tiny: "",
            onClick: _withModifiers(($event: any) => (goToStory($event, item.type, item.contentItem.id)), ["prevent"])
          }, {
            default: _withCtx(() => _cache[5] || (_cache[5] = [
              _createTextVNode("Story")
            ])),
            _: 2
          }, 1032, ["onClick"])
        ]),
        "item.status": _withCtx(({ item, value }) => [
          (value !== '-')
            ? (_openBlock(), _createBlock(_component_v_chip, {
                key: 0,
                density: "compact",
                variant: "flat",
                color: "primary"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t(
              `initiative.edit.status_${_unref(getShortStatus)(
                item.contentItem as any,
                value,
              )}`,
            )), 1)
                ]),
                _: 2
              }, 1024))
            : (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(value), 1))
        ]),
        _: 1
      }, 8, ["items"]),
      _createVNode(_component_v_menu, null, {
        activator: _withCtx(({ props }) => [
          _createVNode(_component_v_btn, _mergeProps({ color: "success" }, props), {
            default: _withCtx(() => _cache[6] || (_cache[6] = [
              _createTextVNode("Add Content")
            ])),
            _: 2
          }, 1040)
        ]),
        default: _withCtx(() => [
          _createVNode(_component_v_list, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_list_item, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_list_item_title, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_btn, {
                        onClick: _cache[0] || (_cache[0] = () => addItem(_unref(TYPE_ACTION))),
                        block: "",
                        variant: "elevated"
                      }, {
                        default: _withCtx(() => _cache[7] || (_cache[7] = [
                          _createTextVNode("Action")
                        ])),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_list_item, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_list_item_title, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_btn, {
                        onClick: _cache[1] || (_cache[1] = () => addItem(_unref(TYPE_INITIATIVE))),
                        block: "",
                        variant: "elevated"
                      }, {
                        default: _withCtx(() => _cache[8] || (_cache[8] = [
                          _createTextVNode("Initiative")
                        ])),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_list_item, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_list_item_title, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_btn, {
                        onClick: _cache[2] || (_cache[2] = () => addItem(_unref(TYPE_INFOGRAPHIC))),
                        block: "",
                        variant: "elevated"
                      }, {
                        default: _withCtx(() => _cache[9] || (_cache[9] = [
                          _createTextVNode("Infographic")
                        ])),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})